// services/surveyService.js — Staff Survey storage (SharePoint + local demo) and invitation emails
//
// Three SharePoint lists back the survey module:
//   SCMSurveys            — one item per survey; questions and settings live in SurveyJson
//   SCMSurveyAssignments  — one item per share (staff × survey × round)
//   SCMSurveyResponses    — one item per submitted response, tied to an assignment
//
// A staff member may answer an assignment exactly once. Re-sharing a survey with
// them creates a new assignment (round + 1), which is what opens it up again.
import { getSiteId, fetchAllPages, GRAPH_PAGE_SIZE } from "./sharepointService";
import { mailSendRequest } from "../authConfig";

const graphBase = "https://graph.microsoft.com/v1.0";
const STORAGE_KEY = "lekoil_survey_db";

export const SURVEY_LISTS = {
  surveys:     "SCMSurveys",
  assignments: "SCMSurveyAssignments",
  responses:   "SCMSurveyResponses",
};

export function generateId(prefix = "sv") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseJson(raw, fallback) {
  if (!raw) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
}

// ── Mappers ────────────────────────────────────────────────────────────────────

function toSurvey(fields, itemId) {
  const body = parseJson(fields.SurveyJson, {});
  return {
    id:            String(itemId),
    _spItemId:     itemId,
    title:         fields.Title || "Untitled survey",
    status:        fields.Status || "draft",
    description:   body.description || "",
    category:      body.category || "",
    questions:     Array.isArray(body.questions) ? body.questions : [],
    settings:      body.settings || {},
    createdBy:     body.createdBy || "",
    createdByName: body.createdByName || "",
    createdAt:     body.createdAt || fields.Created || new Date().toISOString(),
    updatedAt:     body.updatedAt || fields.Modified || new Date().toISOString(),
  };
}

function surveyToFields(s) {
  return {
    Title:  s.title,
    Status: s.status,
    SurveyJson: JSON.stringify({
      description:   s.description || "",
      category:      s.category || "",
      questions:     s.questions || [],
      settings:      s.settings || {},
      createdBy:     s.createdBy || "",
      createdByName: s.createdByName || "",
      createdAt:     s.createdAt,
      updatedAt:     s.updatedAt,
    }),
  };
}

function toAssignment(fields, itemId) {
  return {
    id:           String(itemId),
    _spItemId:    itemId,
    surveyId:     String(fields.SurveyId || ""),
    email:        (fields.Title || "").toLowerCase(),
    name:         fields.StaffName || fields.Title || "",
    department:   fields.Department || "",
    round:        Number(fields.Round) || 1,
    sharedBy:     fields.SharedBy || "",
    sharedByName: fields.SharedByName || "",
    sharedAt:     fields.SharedAt || fields.Created || new Date().toISOString(),
    dueDate:      (fields.DueDate || "").split("T")[0],
  };
}

function assignmentToFields(a) {
  return {
    Title:        a.email,
    SurveyId:     a.surveyId,
    StaffName:    a.name || "",
    Department:   a.department || "",
    Round:        a.round,
    SharedBy:     a.sharedBy || "",
    SharedByName: a.sharedByName || "",
    SharedAt:     a.sharedAt,
    DueDate:      a.dueDate || "",
  };
}

function toResponse(fields, itemId) {
  return {
    id:           String(itemId),
    _spItemId:    itemId,
    surveyId:     String(fields.SurveyId || ""),
    assignmentId: String(fields.AssignmentId || ""),
    email:        (fields.Title || "").toLowerCase(),
    name:         fields.RespondentName || "",
    department:   fields.Department || "",
    round:        Number(fields.Round) || 1,
    answers:      parseJson(fields.AnswersJson, {}),
    submittedAt:  fields.SubmittedAt || fields.Created || new Date().toISOString(),
    durationSec:  Number(fields.DurationSec) || 0,
  };
}

function responseToFields(r) {
  return {
    Title:          r.email,
    SurveyId:       r.surveyId,
    AssignmentId:   r.assignmentId,
    RespondentName: r.name || "",
    Department:     r.department || "",
    Round:          r.round,
    AnswersJson:    JSON.stringify(r.answers || {}),
    SubmittedAt:    r.submittedAt,
    DurationSec:    r.durationSec || 0,
  };
}

// ── Generic list helpers ───────────────────────────────────────────────────────

async function listAll(token, list) {
  const siteId = await getSiteId(token);
  return fetchAllPages(
    token,
    `${graphBase}/sites/${siteId}/lists/${list}/items?expand=fields&$top=${GRAPH_PAGE_SIZE}`,
    { errorMessage: `Failed to load ${list}` },
  );
}

async function createItem(token, list, fields) {
  const siteId = await getSiteId(token);
  const res = await fetch(`${graphBase}/sites/${siteId}/lists/${list}/items`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Create in ${list} failed (${res.status})`);
  }
  return res.json();
}

async function updateItem(token, list, spItemId, fields) {
  const siteId = await getSiteId(token);
  const res = await fetch(`${graphBase}/sites/${siteId}/lists/${list}/items/${spItemId}/fields`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Update in ${list} failed (${res.status})`);
  }
}

async function deleteItem(token, list, spItemId) {
  const siteId = await getSiteId(token);
  const res = await fetch(`${graphBase}/sites/${siteId}/lists/${list}/items/${spItemId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 404) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Delete in ${list} failed (${res.status})`);
  }
}

// ── SharePoint API ─────────────────────────────────────────────────────────────

export async function fetchSurveyDb(token) {
  const [s, a, r] = await Promise.all([
    listAll(token, SURVEY_LISTS.surveys),
    listAll(token, SURVEY_LISTS.assignments),
    listAll(token, SURVEY_LISTS.responses),
  ]);
  return {
    surveys:     s.map((i) => toSurvey(i.fields, i.id)),
    assignments: a.map((i) => toAssignment(i.fields, i.id)),
    responses:   r.map((i) => toResponse(i.fields, i.id)),
  };
}

export async function createSurvey(token, survey) {
  const item = await createItem(token, SURVEY_LISTS.surveys, surveyToFields(survey));
  return toSurvey(item.fields, item.id);
}

export async function updateSurvey(token, survey) {
  await updateItem(token, SURVEY_LISTS.surveys, survey._spItemId, surveyToFields(survey));
  return survey;
}

export async function deleteSurveyItem(token, survey) {
  await deleteItem(token, SURVEY_LISTS.surveys, survey._spItemId);
}

export async function createAssignment(token, assignment) {
  const item = await createItem(token, SURVEY_LISTS.assignments, assignmentToFields(assignment));
  return toAssignment(item.fields, item.id);
}

export async function deleteAssignmentItem(token, assignment) {
  await deleteItem(token, SURVEY_LISTS.assignments, assignment._spItemId);
}

export async function createResponse(token, response) {
  const item = await createItem(token, SURVEY_LISTS.responses, responseToFields(response));
  return toResponse(item.fields, item.id);
}

export async function deleteResponseItem(token, response) {
  await deleteItem(token, SURVEY_LISTS.responses, response._spItemId);
}

// ── localStorage (demo mode) ───────────────────────────────────────────────────

export function loadLocalSurveyDb() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        surveys:     Array.isArray(parsed.surveys) ? parsed.surveys : [],
        assignments: Array.isArray(parsed.assignments) ? parsed.assignments : [],
        responses:   Array.isArray(parsed.responses) ? parsed.responses : [],
      };
    }
  } catch {}
  return { surveys: [], assignments: [], responses: [] };
}

export function saveLocalSurveyDb(db) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(db)); } catch {}
}

// ── Invitation / reminder emails ───────────────────────────────────────────────

async function getMailToken(instance, accounts) {
  try {
    const r = await instance.acquireTokenSilent({ ...mailSendRequest, account: accounts[0] });
    return r.accessToken;
  } catch {
    const r = await instance.acquireTokenPopup({ ...mailSendRequest, account: accounts[0] });
    return r.accessToken;
  }
}

function escHtml(str) {
  if (!str) return "";
  return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function invitationHtml({ survey, recipientName, link, message, dueDate, senderName, isReminder }) {
  const dueFmt = dueDate
    ? new Date(dueDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";
  const questionCount = (survey.questions || []).filter((q) => q.type !== "section").length;
  const minutes = Math.max(1, Math.round(questionCount * 0.4));
  return `
<div style="font-family:Segoe UI,Arial,sans-serif;max-width:640px;margin:0 auto;background:#fff;">
  <div style="background:linear-gradient(135deg,#001A3D,#002D7A);padding:28px 32px;border-radius:12px 12px 0 0;">
    <div style="color:#C8D8FF;font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;">${isReminder ? "Reminder · Staff Survey" : "Staff Survey"}</div>
    <div style="color:#fff;font-size:21px;font-weight:700;margin-top:6px;">${escHtml(survey.title)}</div>
  </div>
  <div style="padding:28px 32px;border:1px solid #E0E7FF;border-top:none;border-radius:0 0 12px 12px;">
    <p style="margin:0 0 18px;font-size:14px;color:#201F1E;">Dear <strong>${escHtml(recipientName)}</strong>,</p>
    <p style="margin:0 0 18px;font-size:14px;color:#333;line-height:1.7;">
      ${isReminder
        ? "This is a friendly reminder that your response to the survey below is still outstanding."
        : "You have been invited to take part in the survey below. Your feedback helps us improve how we work."}
    </p>
    ${message ? `<div style="background:#F8F9FF;border-left:4px solid #0066FF;border-radius:8px;padding:14px 18px;margin:0 0 20px;font-size:14px;color:#333;line-height:1.6;white-space:pre-line;">${escHtml(message)}</div>` : ""}
    ${survey.description ? `<p style="margin:0 0 20px;font-size:13px;color:#555;line-height:1.6;">${escHtml(survey.description)}</p>` : ""}
    <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
      <tr>
        <td style="padding:10px 14px;background:#F8F9FF;border:1px solid #E0E7FF;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;width:34%;">Questions</td>
        <td style="padding:10px 14px;border:1px solid #E0E7FF;border-left:none;font-size:14px;color:#201F1E;">${questionCount} (about ${minutes} min)</td>
      </tr>
      ${dueFmt ? `<tr>
        <td style="padding:10px 14px;background:#F8F9FF;border:1px solid #E0E7FF;border-top:none;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;">Please respond by</td>
        <td style="padding:10px 14px;border:1px solid #E0E7FF;border-left:none;border-top:none;font-size:14px;color:#201F1E;font-weight:600;">${escHtml(dueFmt)}</td>
      </tr>` : ""}
      ${survey.settings?.anonymous ? `<tr>
        <td style="padding:10px 14px;background:#F8F9FF;border:1px solid #E0E7FF;border-top:none;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;">Privacy</td>
        <td style="padding:10px 14px;border:1px solid #E0E7FF;border-left:none;border-top:none;font-size:14px;color:#201F1E;">Anonymous — your name is not shown in the results</td>
      </tr>` : ""}
    </table>
    <div style="text-align:center;margin:0 0 24px;">
      <a href="${link}" style="display:inline-block;background:#0052CC;color:#fff;text-decoration:none;font-weight:600;font-size:15px;padding:13px 32px;border-radius:8px;">Open survey</a>
    </div>
    <p style="margin:0 0 20px;font-size:12px;color:#888;line-height:1.6;">
      Sign in with your LekOil Microsoft 365 account. If the button does not work, copy this link into your browser:<br>
      <a href="${link}" style="color:#0066FF;word-break:break-all;">${link}</a>
    </p>
    <p style="margin:0;font-size:13px;color:#555;">Thank you,<br><strong>${escHtml(senderName)}</strong><br>LekOil</p>
  </div>
</div>`;
}

/**
 * Email each recipient a personal link to their assignment.
 * recipients: [{ email, name, assignmentId }]
 * Returns { sent, failed: [{ email, error }] }.
 */
export async function sendSurveyEmails(instance, accounts, { survey, recipients, message, dueDate, senderName, isReminder = false, onProgress }) {
  const token = await getMailToken(instance, accounts);
  let sent = 0;
  const failed = [];
  for (let i = 0; i < recipients.length; i++) {
    const r = recipients[i];
    const link = `${window.location.origin}/my-surveys/${encodeURIComponent(r.assignmentId)}`;
    try {
      const res = await fetch(`${graphBase}/me/sendMail`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: {
            subject: `${isReminder ? "Reminder: " : ""}${survey.title} — your feedback is requested`,
            body: { contentType: "HTML", content: invitationHtml({ survey, recipientName: r.name || r.email, link, message, dueDate, senderName, isReminder }) },
            toRecipients: [{ emailAddress: { address: r.email, name: r.name || r.email } }],
          },
          saveToSentItems: true,
        }),
      });
      if (!res.ok && res.status !== 202) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      sent++;
    } catch (err) {
      failed.push({ email: r.email, error: err.message });
    }
    onProgress?.(i + 1, recipients.length);
  }
  return { sent, failed };
}
